import { activeSiteConfig } from "@config/site/site-config";
import type { LineItem, Money, ShippingInfo } from "@graphql/generated/types";

export const getMoneyFromCentsAndFractions = (cents = 0, fractions = 0) => {
    if (!cents) return 0;

    return cents / Math.pow(10, fractions);
};

export const roundPrice = (price: number, method: "floor" | "ceil", quantity: number = 1) => {
    const roundingUnit = activeSiteConfig.roundingUnit;
    if (!roundingUnit) {
        return price;
    }

    const scaledRoundingUnit = roundingUnit * 100;
    const pricePerItem = quantity > 1 ? price / quantity : price;

    const roundedPricePerItem =
        method === "floor"
            ? Math.floor(pricePerItem / scaledRoundingUnit) * scaledRoundingUnit
            : Math.ceil(pricePerItem / scaledRoundingUnit) * scaledRoundingUnit;

    return quantity > 1 ? roundedPricePerItem * quantity : roundedPricePerItem;
};

export const calculateDiscount = (
    items: LineItem[],
    subTotalPrice: number,
    shippingPrice: number
) => {
    let totalPriceAfterDiscount = 0;
    items.forEach((lineItem) => {
        totalPriceAfterDiscount += roundPrice(
            lineItem.totalPrice.centAmount,
            "floor",
            lineItem.quantity
        );
    });

    return subTotalPrice - shippingPrice - totalPriceAfterDiscount;
};

// Applies the active market's rounding rule (e.g. nearest 5 HUF in Hungary) to the
// cart total. Mirrors Summary's calculation so the payment-step "Pay this amount"
// label and the Adyen-submitted amount stay consistent with the sidebar total.
// Runs whenever the market requires rounding — independent of whether a discount
// code is applied. When there's no real discount, `discount` is null so the
// Summary discount row stays hidden (the rounding remainder is folded into the total).
// Returns null when rounding does not apply.
export const getRoundedCartTotal = ({
    totalGross,
    subtotalPrice,
    items,
    shippingInfo,
    cartDiscount,
}: {
    totalGross?: Money | null;
    subtotalPrice?: Money | null;
    items: LineItem[];
    shippingInfo?: ShippingInfo | null;
    cartDiscount?: Money | null;
}): { total: Money; discount: Money | null } | null => {
    if (!activeSiteConfig.roundingUnit || !totalGross || !subtotalPrice) {
        return null;
    }

    const shippingCentAmount =
        shippingInfo?.discountedPrice?.value?.centAmount ?? shippingInfo?.price?.centAmount ?? 0;

    const discountCentAmount = calculateDiscount(
        items,
        subtotalPrice.centAmount,
        shippingCentAmount
    );

    return {
        total: { ...totalGross, centAmount: subtotalPrice.centAmount - discountCentAmount },
        discount: cartDiscount ? { ...cartDiscount, centAmount: discountCentAmount } : null,
    };
};
